import { useEffect, useMemo, useRef, useState } from 'react';
import { elementColor } from '../elements';
import { useAnimationFrame } from '../useAnimationFrame';
import {
  cueHighlights,
  cueIsActive,
  DARK_INK,
  NowYouTry,
  usePrefersReducedMotion,
  VizCard,
  VIZ_BTN_GHOST,
  VIZ_FOCUS,
  type VizCue,
} from '../vizShared';

// VseprGeometry — pseudo-3D molecular-geometry viewer. Given a molecule (or a
// raw steric description: bonding + lone pairs), it places electron domains at
// their ideal VSEPR positions in 3D, projects them to 2D with a rotating camera,
// and labels the bond angle. A toggle switches between the electron-domain
// geometry (lone pairs shown as translucent lobes) and the molecular shape
// (lone pairs hidden). Pointer/touch draggable + keyboard-arrow rotatable.
//
// Self-demo: a brief auto-rotation on mount (a confident half-spin) then settles
// and offers "Now you try". Cancelled on first interaction.
//
// cue: { play? → run auto-rotation; progress? → set the Y rotation angle (0..1
//        maps to a full turn); highlight? → pick a preset molecule by name;
//        step? → step through the preset list }
//
// config: { molecule?, bonding?, lone?, central?, terminal?, title?, autoRotate? }

export interface VseprGeometryConfig {
  title?: string;
  molecule?: string;
  bonding?: number; // bonding domains (sigma)
  lone?: number; // lone pairs on central atom
  central?: string;
  terminal?: string;
  // Explicit per-terminal element labels (e.g. CH2O → ['H','H','O']). When
  // present, each bonding position is labelled with its OWN element from this
  // list (by index, cycling if short), instead of repeating `terminal`. The
  // shape/angle/domain-count logic is unaffected.
  terminals?: string[];
  autoRotate?: boolean;
}

interface Props {
  config?: VseprGeometryConfig;
  onComplete?: (result?: unknown) => void;
  cue?: VizCue;
}

interface Vec3 { x: number; y: number; z: number; }

interface ShapeDef {
  name: string; // molecular shape
  domainName: string; // electron-domain geometry
  angle: string;
  // Unit vectors for ALL electron domains (bonding first, then lone pairs).
  domains: Vec3[];
  bonding: number; // first N domains are bonds
}

// Ideal domain directions per steric number. Lone pairs occupy specific slots
// (e.g. equatorial for trigonal bipyramidal) so the resulting shape is correct.
function shapeFor(bonding: number, lone: number): ShapeDef {
  const steric = bonding + lone;
  const v = (x: number, y: number, z: number): Vec3 => {
    const m = Math.hypot(x, y, z) || 1;
    return { x: x / m, y: y / m, z: z / m };
  };

  if (steric <= 1) {
    return { name: 'Linear', domainName: 'Linear', angle: '—', bonding, domains: [v(0, 0, 1)] };
  }
  if (steric === 2) {
    const d = [v(0, 0, 1), v(0, 0, -1)];
    return { name: 'Linear', domainName: 'Linear', angle: '180°', bonding, domains: d };
  }
  if (steric === 3) {
    // Trigonal in the xz-plane.
    const d = [0, 1, 2].map((i) => {
      const a = (i / 3) * Math.PI * 2 - Math.PI / 2;
      return v(Math.cos(a), 0, Math.sin(a));
    });
    const name = lone === 0 ? 'Trigonal planar' : 'Bent';
    return { name, domainName: 'Trigonal planar', angle: lone === 0 ? '120°' : '~118°', bonding, domains: d };
  }
  if (steric === 4) {
    // Tetrahedron.
    const d = [v(1, 1, 1), v(1, -1, -1), v(-1, 1, -1), v(-1, -1, 1)];
    const name = lone === 0 ? 'Tetrahedral' : lone === 1 ? 'Trigonal pyramidal' : 'Bent';
    const angle = lone === 0 ? '109.5°' : lone === 1 ? '~107°' : '~104.5°';
    return { name, domainName: 'Tetrahedral', angle, bonding, domains: d };
  }
  if (steric === 5) {
    // Trigonal bipyramidal: 3 equatorial + 2 axial. Lone pairs fill equatorial.
    const eq = [0, 1, 2].map((i) => {
      const a = (i / 3) * Math.PI * 2;
      return v(Math.cos(a), 0, Math.sin(a));
    });
    const ax = [v(0, 1, 0), v(0, -1, 0)];
    // Order so lone pairs (placed last) take equatorial slots: put equatorial last.
    const ordered = [...ax, ...eq];
    const name =
      lone === 0 ? 'Trigonal bipyramidal' : lone === 1 ? 'Seesaw' : lone === 2 ? 'T-shaped' : 'Linear';
    return { name, domainName: 'Trigonal bipyramidal', angle: '90° / 120°', bonding, domains: ordered };
  }
  // steric 6+ : octahedral.
  const d = [v(1, 0, 0), v(-1, 0, 0), v(0, 1, 0), v(0, -1, 0), v(0, 0, 1), v(0, 0, -1)];
  const name = lone === 0 ? 'Octahedral' : lone === 1 ? 'Square pyramidal' : 'Square planar';
  return { name, domainName: 'Octahedral', angle: '90°', bonding, domains: d };
}

// Common-molecule presets → (central, terminal, bonding, lone). `terminals`
// (optional) gives an explicit per-position element list for molecules whose
// outer atoms are NOT all the same element (e.g. formaldehyde CH2O).
interface Preset { central: string; terminal: string; bonding: number; lone: number; terminals?: string[]; }
const PRESETS: Record<string, Preset> = {
  'CO2': { central: 'C', terminal: 'O', bonding: 2, lone: 0 },
  'BeCl2': { central: 'Be', terminal: 'Cl', bonding: 2, lone: 0 },
  'BF3': { central: 'B', terminal: 'F', bonding: 3, lone: 0 },
  'SO2': { central: 'S', terminal: 'O', bonding: 2, lone: 1 },
  'CH2O': { central: 'C', terminal: 'O', bonding: 3, lone: 0, terminals: ['H', 'H', 'O'] },
  'CH4': { central: 'C', terminal: 'H', bonding: 4, lone: 0 },
  'NH3': { central: 'N', terminal: 'H', bonding: 3, lone: 1 },
  'H2O': { central: 'O', terminal: 'H', bonding: 2, lone: 2 },
  'PCl5': { central: 'P', terminal: 'Cl', bonding: 5, lone: 0 },
  'SF4': { central: 'S', terminal: 'F', bonding: 4, lone: 1 },
  'ClF3': { central: 'Cl', terminal: 'F', bonding: 3, lone: 2 },
  'SF6': { central: 'S', terminal: 'F', bonding: 6, lone: 0 },
  'BrF5': { central: 'Br', terminal: 'F', bonding: 5, lone: 1 },
  'XeF4': { central: 'Xe', terminal: 'F', bonding: 4, lone: 2 },
};
const PRESET_KEYS = Object.keys(PRESETS);

const SIZE = 300;
const C = SIZE / 2;
const BOND_LEN = 95;

// Rotate a vector around Y then X.
function rotate(p: Vec3, ry: number, rx: number): Vec3 {
  const cy = Math.cos(ry), sy = Math.sin(ry);
  let x = p.x * cy + p.z * sy;
  let z = -p.x * sy + p.z * cy;
  const y0 = p.y;
  const cx = Math.cos(rx), sx = Math.sin(rx);
  const y = y0 * cx - z * sx;
  z = y0 * sx + z * cx;
  return { x, y, z };
}

export default function VseprGeometry({ config = {}, onComplete, cue }: Props) {
  const reduced = usePrefersReducedMotion();
  const hasCue = cueIsActive(cue);

  const initialKey = useMemo(() => {
    const m = (config.molecule ?? '').replace(/\s+/g, '');
    if (m && PRESETS[m]) return m;
    const ci = PRESET_KEYS.find((k) => k.toLowerCase() === m.toLowerCase());
    return ci ?? (config.bonding != null ? '' : 'H2O');
  }, [config.molecule, config.bonding]);

  const [key, setKey] = useState(initialKey);
  const [showDomains, setShowDomains] = useState(false); // electron-domain vs molecular shape
  // Self-demo spins on mount unless reduced motion or a cue is driving. After the
  // demo we honour the config.autoRotate default (off unless asked).
  const [autoRotate, setAutoRotate] = useState(
    reduced || hasCue ? (config.autoRotate ?? false) : true,
  );
  const [ry, setRy] = useState(0.6);
  const [rx, setRx] = useState(-0.35);
  const [demoDone, setDemoDone] = useState(reduced || hasCue);
  const dragRef = useRef<{ x: number; y: number } | null>(null);
  const interactedRef = useRef(false);
  const demoElapsedRef = useRef(0);

  const markInteracted = () => {
    if (!demoDone) {
      setDemoDone(true);
      setAutoRotate(config.autoRotate ?? false);
    }
    if (!interactedRef.current) {
      interactedRef.current = true;
      onComplete?.({ type: 'vsepr_geometry', molecule: key || 'custom' });
    }
  };

  // ----- Cue handling -----
  const highlights = useMemo(() => cueHighlights(cue), [cue]);
  // cue.highlight → pick a preset by name.
  useEffect(() => {
    if (highlights.length === 0) return;
    const match = PRESET_KEYS.find((k) => highlights.includes(k.toLowerCase()));
    if (match) setKey(match);
  }, [highlights]);
  // cue.step → step through presets.
  useEffect(() => {
    if (cue?.step == null) return;
    const i = ((Math.round(cue.step) % PRESET_KEYS.length) + PRESET_KEYS.length) % PRESET_KEYS.length;
    setKey(PRESET_KEYS[i]);
  }, [cue?.step]);
  // cue.progress (0..1) → set Y rotation directly (one full turn).
  useEffect(() => {
    if (cue?.progress == null) return;
    setAutoRotate(false);
    setRy(Math.max(0, Math.min(1, cue.progress)) * Math.PI * 2);
  }, [cue?.progress]);
  // cue.play → drive the rotation.
  useEffect(() => {
    if (cue?.play == null) return;
    setAutoRotate(cue.play);
  }, [cue?.play]);

  // Resolve current preset / custom config.
  const preset: Preset = key && PRESETS[key]
    ? PRESETS[key]
    : {
        central: config.central ?? 'A',
        terminal: config.terminal ?? 'X',
        bonding: Math.max(1, config.bonding ?? 2),
        lone: Math.max(0, config.lone ?? 2),
        terminals: Array.isArray(config.terminals) ? config.terminals : undefined,
      };

  const shape = useMemo(() => shapeFor(preset.bonding, preset.lone), [preset.bonding, preset.lone]);

  // Per-terminal element label for the i-th BONDING domain. Falls back to the
  // single `terminal` when no explicit list is given (or an entry is missing),
  // so existing single-terminal molecules render exactly as before. Resilient:
  // never throws on a short/empty/malformed list.
  const terminalAt = (bondIndex: number): string => {
    const list = preset.terminals;
    if (Array.isArray(list) && list.length > 0) {
      const el = list[bondIndex % list.length];
      if (typeof el === 'string' && el.trim()) return el;
    }
    return preset.terminal;
  };

  // Continuous rotation: self-demo spin (auto-settles after ~2.2s) or user auto-rotate.
  useAnimationFrame((dt) => {
    if (dragRef.current) return;
    if (!demoDone) {
      demoElapsedRef.current += dt;
      setRy((r) => r + dt * 1.3);
      if (demoElapsedRef.current >= 2.2) {
        setDemoDone(true);
        setAutoRotate(config.autoRotate ?? false);
      }
      return;
    }
    if (autoRotate) setRy((r) => r + dt * 0.5);
  }, !reduced && (!demoDone || autoRotate));

  // Project the domains and sort by depth (painter's algorithm).
  const projected = useMemo(() => {
    return shape.domains.map((d, i) => {
      const r = rotate(d, ry, rx);
      const isBond = i < shape.bonding;
      return {
        i,
        isBond,
        x: C + r.x * BOND_LEN,
        y: C + r.y * BOND_LEN,
        z: r.z, // depth for sorting + scaling
      };
    });
  }, [shape, ry, rx]);

  const sorted = [...projected].sort((a, b) => a.z - b.z);

  // For the angle label, pick two visible bonds.
  const visibleBonds = projected.filter((p) => p.isBond);

  const onPointerDown = (e: React.PointerEvent) => {
    dragRef.current = { x: e.clientX, y: e.clientY };
    (e.target as Element).setPointerCapture?.(e.pointerId);
    markInteracted();
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.x;
    const dy = e.clientY - dragRef.current.y;
    dragRef.current = { x: e.clientX, y: e.clientY };
    setRy((r) => r + dx * 0.01);
    setRx((r) => Math.max(-1.4, Math.min(1.4, r + dy * 0.01)));
  };
  const onPointerUp = () => { dragRef.current = null; };

  const onKeyDown = (e: React.KeyboardEvent) => {
    const step = 0.18;
    let handled = true;
    if (e.key === 'ArrowLeft') setRy((r) => r - step);
    else if (e.key === 'ArrowRight') setRy((r) => r + step);
    else if (e.key === 'ArrowUp') setRx((r) => Math.max(-1.4, r - step));
    else if (e.key === 'ArrowDown') setRx((r) => Math.min(1.4, r + step));
    else handled = false;
    if (handled) {
      e.preventDefault();
      markInteracted();
    }
  };

  // Normalised Y angle in degrees for aria-valuenow.
  const ryDeg = Math.round(((ry % (Math.PI * 2)) * 180) / Math.PI);

  // Backstop: a molecule we have no preset for (and no custom domain spec) used
  // to silently render as H2O. Show the formula instead. Authors should use a
  // preset, supply central/terminal/bonding/lone, or pick chemical_formula.
  const requestedMolecule = (config.molecule ?? '').replace(/\s+/g, '');
  const moleculeSupported =
    !requestedMolecule ||
    config.bonding != null ||
    config.central != null ||
    PRESET_KEYS.some((k) => k.toLowerCase() === requestedMolecule.toLowerCase());
  if (!moleculeSupported) {
    return (
      <VizCard eyebrow="Bonding · VSEPR" title={config.title ?? 'VSEPR molecular geometry'} reducedMotion={reduced}>
        <div className="grid w-full place-items-center rounded-md border border-ws-border bg-ws-surface-2 p-8 text-center">
          <div className="font-mono text-3xl font-bold tracking-wide text-ws-ink">{requestedMolecule}</div>
          <p className="mt-2 text-xs text-ws-ink-3">A 3-D VSEPR geometry isn’t available for this molecule — showing its formula.</p>
        </div>
      </VizCard>
    );
  }

  return (
    <VizCard
      eyebrow="Bonding · VSEPR"
      title={config.title ?? 'VSEPR molecular geometry'}
      subtitle="Electron domains repel into 3-D shapes"
      reducedMotion={reduced}
      hint="Drag to rotate · ←→↑↓ to spin · toggle lone-pair domains · auto-rotates on load"
    >
      <div className="flex w-full flex-col gap-3 lg:flex-row">
        <div className="relative flex-shrink-0">
          <NowYouTry show={demoDone && !hasCue && !reduced} label="Drag to rotate" />
          <svg
            viewBox={`0 0 ${SIZE} ${SIZE}`}
            className={`w-full max-w-[300px] cursor-grab select-none rounded-md border border-ws-border bg-ws-surface-2 active:cursor-grabbing ${VIZ_FOCUS}`}
            style={{ touchAction: 'none' }}
            role="slider"
            tabIndex={0}
            aria-label={`VSEPR geometry of ${key || 'molecule'}: ${shape.name}. Rotate with arrow keys.`}
            aria-valuemin={0}
            aria-valuemax={360}
            aria-valuenow={((ryDeg % 360) + 360) % 360}
            aria-valuetext={`Rotated ${((ryDeg % 360) + 360) % 360} degrees`}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerLeave={onPointerUp}
            onKeyDown={onKeyDown}
          >
            {/* Bonds (drawn first, behind atoms) */}
            {sorted.map((p) =>
              p.isBond ? (
                <line
                  key={`l${p.i}`}
                  x1={C}
                  y1={C}
                  x2={p.x}
                  y2={p.y}
                  stroke="var(--ws-ink-3)"
                  strokeWidth={3 * (0.7 + (p.z + 1) / 2 * 0.5)}
                  strokeLinecap="round"
                  opacity={0.55 + (p.z + 1) / 2 * 0.45}
                />
              ) : null,
            )}

            {/* Domains: bonding atoms or lone-pair lobes */}
            {sorted.map((p) => {
              const depthScale = 0.7 + (p.z + 1) / 2 * 0.6; // farther = smaller
              if (p.isBond) {
                const r = 15 * depthScale;
                const el = terminalAt(p.i); // p.i is the bond index (bonds are first in domains)
                return (
                  <g key={`a${p.i}`}>
                    <circle cx={p.x} cy={p.y} r={r} fill={elementColor(el)} stroke="rgba(0,0,0,0.25)" strokeWidth="1" opacity={0.6 + (p.z + 1) / 2 * 0.4} />
                    <text x={p.x} y={p.y + r * 0.3} textAnchor="middle" fontSize={11 * depthScale} fontWeight="700" style={{ fill: DARK_INK }}>
                      {el}
                    </text>
                  </g>
                );
              }
              // Lone-pair lobe — only when showing the electron-domain geometry.
              if (!showDomains) return null;
              const r = 16 * depthScale;
              return (
                <ellipse
                  key={`lp${p.i}`}
                  cx={p.x}
                  cy={p.y}
                  rx={r}
                  ry={r * 0.7}
                  fill="var(--ws-accent)"
                  opacity={0.28}
                  transform={`rotate(${(Math.atan2(p.y - C, p.x - C) * 180) / Math.PI} ${p.x} ${p.y})`}
                />
              );
            })}

            {/* Central atom (always on top) */}
            <circle cx={C} cy={C} r="20" fill={elementColor(preset.central)} stroke="rgba(0,0,0,0.3)" strokeWidth="1.5" />
            <text x={C} y={C + 5} textAnchor="middle" fontSize="14" fontWeight="800" style={{ fill: DARK_INK }}>
              {preset.central}
            </text>

            {/* Bond-angle annotation */}
            {visibleBonds.length >= 2 && (
              <text x={C} y={SIZE - 12} textAnchor="middle" fontSize="12" fontWeight="600" fill="var(--ws-ink-2)">
                Bond angle: {shape.angle}
              </text>
            )}
          </svg>
        </div>

        {/* Controls + readout */}
        <div className="flex min-w-0 flex-1 flex-col gap-3">
          <div>
            <label className="mb-1 block text-[10px] font-medium uppercase tracking-wide text-ws-ink-3" htmlFor="vsepr-molecule">
              Molecule
            </label>
            <select
              id="vsepr-molecule"
              value={key}
              onChange={(e) => { setKey(e.target.value); markInteracted(); }}
              className={`w-full rounded-sm border border-ws-border bg-ws-tile px-2 py-1.5 text-sm font-medium text-ws-ink transition-colors hover:border-ws-border-strong ${VIZ_FOCUS}`}
            >
              {!PRESETS[key] && <option value={key}>Custom (AXₙEₘ)</option>}
              {PRESET_KEYS.map((k) => (
                <option key={k} value={k}>{k}</option>
              ))}
            </select>
          </div>

          <dl className="grid grid-cols-2 gap-x-4 gap-y-1 rounded-sm border border-ws-border bg-ws-tile p-2 text-xs text-ws-ink-2">
            <div className="col-span-2"><dt className="inline text-ws-ink-3">Molecular shape: </dt><dd className="inline font-semibold text-ws-ink">{shape.name}</dd></div>
            <div className="col-span-2"><dt className="inline text-ws-ink-3">Domain geometry: </dt><dd className="inline font-medium">{shape.domainName}</dd></div>
            <div><dt className="inline text-ws-ink-3">Bonding: </dt><dd className="inline font-medium">{preset.bonding}</dd></div>
            <div><dt className="inline text-ws-ink-3">Lone pairs: </dt><dd className="inline font-medium">{preset.lone}</dd></div>
            <div className="col-span-2"><dt className="inline text-ws-ink-3">Steric no.: </dt><dd className="inline font-medium">{preset.bonding + preset.lone}</dd></div>
          </dl>

          <div className="flex flex-wrap gap-1.5">
            <button
              type="button"
              onClick={() => { setShowDomains((v) => !v); markInteracted(); }}
              aria-pressed={showDomains}
              className={`${VIZ_BTN_GHOST} ${showDomains ? '!bg-ws-accent !text-ws-on-accent !border-ws-accent' : ''}`}
            >
              {showDomains ? 'Electron domains' : 'Molecular shape'}
            </button>
            <button
              type="button"
              onClick={() => { markInteracted(); setAutoRotate((v) => !v); }}
              aria-pressed={autoRotate}
              disabled={reduced}
              className={`${VIZ_BTN_GHOST} ${autoRotate ? '!bg-ws-accent !text-ws-on-accent !border-ws-accent' : ''}`}
            >
              {autoRotate ? 'Auto-rotate: on' : 'Auto-rotate: off'}
            </button>
          </div>

          <p className="text-[11px] leading-snug text-ws-ink-3">
            Drag the model or use arrow keys to rotate. Toggle to reveal lone-pair
            domains (translucent lobes) that bend the molecular shape away from the
            ideal domain geometry.
          </p>
        </div>
      </div>
    </VizCard>
  );
}
